/**
 * Parent-portal login (ADR-0015) — the tenant-branded front door for "one
 * login, many apps". Slug-less (`/portal/login`): it authenticates the
 * GLOBAL identity (no app context, no membership gate — choice #2) and lands
 * on the launcher. There is NO sign-up here (signup stays per-app — a parent
 * login can't assign an app role).
 *
 * Mirrors the per-app `PortalAuthPage` look (split-screen hero + form card,
 * pinned `.tray-light`) but wears the TENANT brand from the public
 * `getParentBrand` payload, not an app's.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { TotpEnroll } from "@/components/auth/totp";
import { ApiError } from "@/lib/api";
import { portalSetupAdapter } from "@/lib/api-2fa";
import { portalApi, type ParentAuthResult } from "@/lib/api-portal";
import { parentLauncherPath, parentSignupPath, usePortalAuth } from "@/lib/portalAuth";

/** Pick a readable text colour for a hex background by luminance, so any
 *  tenant accent keeps the hero text legible. */
function readableOn(bg: string | undefined): string {
  const m = bg && /^#?([0-9a-fA-F]{6})$/.exec(bg.trim());
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.6 ? "#0f172a" : "#ffffff";
}

export function ParentPortalLoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = usePortalAuth((s) => s.setSession);
  const user = usePortalAuth((s) => s.user);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 2FA stages (ADR-0017): password → optional code (totp_required) or forced
  // enrollment (totp_setup_required).
  const [stage, setStage] = useState<
    | { kind: "credentials" }
    | { kind: "totp"; challengeToken: string }
    | { kind: "setup"; setupToken: string; begin: { secret: string; otpauth_uri: string } }
  >({ kind: "credentials" });
  const [code, setCode] = useState("");

  // Default post-login destination is the launcher (choice: tenant login is
  // the only entry that lands on /me).
  const next = params.get("next") || parentLauncherPath();

  // Tenant brand (public — read before auth).
  const brandQ = useQuery({
    queryKey: ["parent-brand"],
    queryFn: () => portalApi.getParentBrand(),
    staleTime: 5 * 60_000,
  });
  const brand = brandQ.data;

  if (user) {
    return <Navigate to={next} replace />;
  }

  function mapError(err: unknown): string {
    const c = err instanceof ApiError ? err.code : "error";
    return c.includes("invalid_credentials")
      ? "Codice o credenziali non validi."
      : "Si è verificato un errore. Riprova.";
  }

  function finish(res: ParentAuthResult) {
    if (res.access_token && res.user) {
      setSession(res.access_token, res.user, res.refresh_token);
      navigate(next, { replace: true });
    }
  }

  // Forced-setup confirm returns a session token but no user — fetch the
  // profile with it, then open the session.
  async function completeWithToken(token: string) {
    usePortalAuth.getState().setSession(token, {
      id: "", email, full_name: null,
    });
    const me = await portalApi.parentMe();
    setSession(token, me);
    navigate(next, { replace: true });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await portalApi.parentLogin({ email, password });
      if (res.status === "totp_required" && res.challenge_token) {
        setStage({ kind: "totp", challengeToken: res.challenge_token });
      } else if (res.status === "totp_setup_required" && res.setup_token) {
        const begin = await portalSetupAdapter(res.setup_token).begin();
        setStage({ kind: "setup", setupToken: res.setup_token, begin });
      } else {
        finish(res);
      }
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  }

  async function onTotp(e: FormEvent) {
    e.preventDefault();
    if (stage.kind !== "totp") return;
    setBusy(true);
    setError(null);
    try {
      finish(
        await portalApi.parentLoginTotp({
          challenge_token: stage.challengeToken,
          code: code.trim(),
        }),
      );
    } catch (err) {
      setError(mapError(err));
    } finally {
      setBusy(false);
    }
  }

  const tenantName = brand?.name || "Portal";
  const accent = brand?.theme.accent || undefined;
  const onAccent = readableOn(accent);
  const pageBg = brand?.theme.background || undefined;
  const heroTitle = brand?.login.title || `Welcome to ${tenantName}`;
  const heroSubtitle =
    brand?.login.subtitle || "Sign in to reach all of your apps in one place.";
  const inputCls =
    "h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground " +
    "outline-none transition placeholder:text-muted-foreground/70 focus:border-foreground/40 " +
    "focus:ring-2 focus:ring-foreground/10";

  return (
    <div className="tray-light min-h-screen w-full bg-background lg:grid lg:grid-cols-2">
      {/* Branded hero — desktop only. */}
      <div
        className="relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between"
        style={{ background: accent || "#0f172a", color: onAccent }}
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-80 w-80 rounded-full bg-black/10 blur-3xl" />
        <div className="relative text-lg font-semibold tracking-tight">{tenantName}</div>
        <div className="relative">
          <h2 className="max-w-md text-4xl font-semibold leading-tight">{heroTitle}</h2>
          <p className="mt-4 max-w-sm text-sm opacity-80">{heroSubtitle}</p>
        </div>
        <div className="relative text-xs opacity-60">{brand?.footer || `© ${tenantName}`}</div>
      </div>

      {/* Form column. */}
      <div
        className="flex min-h-screen items-center justify-center p-6"
        style={pageBg ? { background: pageBg } : undefined}
      >
        <div className="portal-pop w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-xl">
          {brand?.logo_url ? (
            <img
              src={brand.logo_url}
              alt={tenantName}
              className="mb-6 h-10 max-w-[180px] object-contain"
            />
          ) : (
            <div className="mb-6 text-lg font-semibold lg:hidden">{tenantName}</div>
          )}

          {stage.kind === "credentials" && (
          <>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your credentials to reach your apps.
          </p>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                className={inputCls}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Password
              </span>
              <input
                type="password"
                required
                autoComplete="current-password"
                className={inputCls}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={accent ? { background: accent, color: onAccent } : undefined}
              className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {/* Self-signup (ADR-0016) — only when registration is open. */}
          {brand?.signup_enabled && (
            <p className="mt-5 text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => navigate(parentSignupPath())}
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                Create one
              </button>
            </p>
          )}
          </>
          )}

          {stage.kind === "totp" && (
            <form onSubmit={onTotp} className="flex flex-col gap-4">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Verifica in due passaggi
              </h1>
              <p className="-mt-2 text-sm text-muted-foreground">
                Inserisci il codice della tua app authenticator (o un codice di recupero).
              </p>
              <input
                autoFocus
                inputMode="text"
                autoComplete="one-time-code"
                className={inputCls + " text-center font-mono tracking-widest"}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={busy || !code.trim()}
                style={accent ? { background: accent, color: onAccent } : undefined}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Verifica
              </button>
            </form>
          )}

          {stage.kind === "setup" && (
            <div className="flex flex-col gap-3">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                Configura la 2FA
              </h1>
              <p className="-mt-1 text-sm text-muted-foreground">
                Il portale richiede la verifica in due passaggi: configurala per continuare.
              </p>
              <TotpEnroll
                begin={stage.begin}
                onConfirm={(c) => portalSetupAdapter(stage.setupToken).confirm(c)}
                onDone={(result) => {
                  if (result.access_token) void completeWithToken(result.access_token);
                }}
              />
            </div>
          )}

          {/* Footer line — visible on mobile too (the hero is desktop-only). */}
          {brand?.footer && (
            <p className="mt-6 text-center text-xs text-muted-foreground lg:hidden">
              {brand.footer}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
